<div class="space-y-6">
  <app-page-header [backTo]="['/vehicles']" backLabel="Retour à la flotte" />

  @if (loadError() || loading()) {
    <app-data-state [loading]="loading()" [error]="loadError()" (retry)="fetch()" />
  } @else if (vehicle(); as v) {
    <!-- Identity + actions -->
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-4">
          @if (v.imgCover) {
            <img [src]="v.imgCover" [alt]="displayName()" class="h-24 w-32 rounded-lg object-cover" />
          } @else {
            <span class="flex h-24 w-32 items-center justify-center rounded-lg bg-gray-100 text-gray-400 dark:bg-gray-800">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M6.5 5.75A1.25 1.25 0 0 0 5.3 6.62l-1.05 3.13H3.75a.75.75 0 0 0-.75.75V14a.75.75 0 0 0 .75.75H4.5v1.5a1.25 1.25 0 1 0 2.5 0v-1.5h10v1.5a1.25 1.25 0 1 0 2.5 0v-1.5h.75A.75.75 0 0 0 21 14v-3.5a.75.75 0 0 0-.75-.75h-.5L18.7 6.62A1.25 1.25 0 0 0 17.5 5.75h-11Z"/></svg>
            </span>
          }
          <div class="min-w-0">
            <h1 class="text-title-sm font-bold text-gray-800 dark:text-white/90 sm:text-title-md">{{ displayName() }}</h1>
            <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {{ v.year }} · <span class="font-mono">{{ v.licencePlate || '—' }}</span> · {{ v.location || '—' }}
            </p>
            <div class="mt-2 flex flex-wrap items-center gap-2">
              <span
                class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                [class.bg-brand-50]="statusMeta[v.status].palette === 'brand'"
                [class.text-brand-700]="statusMeta[v.status].palette === 'brand'"
                [class.dark:bg-brand-500/15]="statusMeta[v.status].palette === 'brand'"
                [class.dark:text-brand-400]="statusMeta[v.status].palette === 'brand'"
                [class.bg-blue-light-50]="statusMeta[v.status].palette === 'blue-light'"
                [class.text-blue-light-700]="statusMeta[v.status].palette === 'blue-light'"
                [class.dark:bg-blue-light-500/15]="statusMeta[v.status].palette === 'blue-light'"
                [class.dark:text-blue-light-400]="statusMeta[v.status].palette === 'blue-light'"
                [class.bg-warning-50]="statusMeta[v.status].palette === 'warning'"
                [class.text-warning-700]="statusMeta[v.status].palette === 'warning'"
                [class.dark:bg-warning-500/15]="statusMeta[v.status].palette === 'warning'"
                [class.dark:text-warning-400]="statusMeta[v.status].palette === 'warning'"
                [class.bg-gray-100]="statusMeta[v.status].palette === 'gray'"
                [class.text-gray-700]="statusMeta[v.status].palette === 'gray'"
                [class.dark:bg-gray-800]="statusMeta[v.status].palette === 'gray'"
                [class.dark:text-gray-300]="statusMeta[v.status].palette === 'gray'"
                [class.bg-error-50]="statusMeta[v.status].palette === 'error'"
                [class.text-error-700]="statusMeta[v.status].palette === 'error'"
                [class.dark:bg-error-500/15]="statusMeta[v.status].palette === 'error'"
                [class.dark:text-error-400]="statusMeta[v.status].palette === 'error'"
              >
                {{ statusMeta[v.status].label }}
              </span>
              @if (v.avgRating != null && v.avgRating > 0) {
                <span class="inline-flex items-center gap-1 rounded-full bg-warning-50 px-2.5 py-1 text-xs font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                  ★ {{ v.avgRating | number:'1.1-1' }}
                </span>
              }
              @if (isFeatured()) {
                <span class="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">
                  ★ À la une
                </span>
              }
              @if (v.docsExpired) {
                <span class="inline-flex items-center gap-1 rounded-full bg-error-50 px-2.5 py-1 text-xs font-medium text-error-700 dark:bg-error-500/15 dark:text-error-400" title="Un document obligatoire est expiré — véhicule masqué de la recherche">
                  Documents expirés
                </span>
              }
            </div>
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          @if (isUnderReview()) {
            <button type="button" (click)="openApprove()" class="inline-flex items-center gap-2 rounded-lg bg-success-500 px-4 py-2 text-sm font-medium text-white hover:bg-success-600">
              Approuver
            </button>
            <button type="button" (click)="openReject()" class="inline-flex items-center gap-2 rounded-lg border border-error-300 px-4 py-2 text-sm font-medium text-error-700 hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10">
              Rejeter
            </button>
          }
          <button
            type="button"
            (click)="toggleFeatured()"
            [disabled]="featuring()"
            class="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
            [class.bg-brand-500]="!isFeatured()"
            [class.text-white]="!isFeatured()"
            [class.hover:bg-brand-600]="!isFeatured()"
            [class.border]="isFeatured()"
            [class.border-gray-300]="isFeatured()"
            [class.text-gray-700]="isFeatured()"
            [class.hover:bg-gray-50]="isFeatured()"
            [class.dark:border-gray-700]="isFeatured()"
            [class.dark:text-gray-300]="isFeatured()"
            [class.dark:hover:bg-white/5]="isFeatured()"
          >
            @if (featuring()) {
              <svg class="size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path></svg>
              Mise à jour…
            } @else if (isFeatured()) {
              Retirer de la une
            } @else {
              ★ Mettre à la une
            }
          </button>
          <button type="button" (click)="openDelete()" class="inline-flex items-center gap-2 rounded-lg border border-error-300 px-4 py-2 text-sm font-medium text-error-700 transition hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10">
            Supprimer
          </button>
        </div>
      </div>

      @if (featureError()) {
        <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
          {{ featureError() }}
        </div>
      }

      @if (v.status === 'AVAILABLE' && v.rejectionReason) {
        <!-- previously rejected then approved — show history but neutral -->
      } @else if (v.rejectionReason) {
        <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
          <strong>Motif du rejet :</strong> {{ v.rejectionReason }}
        </div>
      }
    </section>

    <!-- Pricing + key stats -->
    <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Tarif / jour</p>
        <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">
          {{ v.dailyRate | number:'1.0-0' }} <span class="text-sm font-normal text-gray-400">XOF</span>
        </p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Carburant</p>
        <p class="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
          {{ v.fuelType ? fuelLabel[v.fuelType] : '—' }}
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400">{{ v.fuelConsumptionRate || '—' }} L/100km</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Boîte</p>
        <p class="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
          {{ v.gearbox ? gearLabel[v.gearbox] : '—' }}
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400">{{ v.cylinderCapacity || '—' }} cc</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Portes</p>
        <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">{{ v.numberOfDoors || '—' }}</p>
      </div>
    </div>

    <!-- Galerie -->
    @if (v.photos?.length) {
      <section>
        <h2 class="mb-3 text-sm font-semibold text-gray-800 dark:text-white/90">Photos</h2>
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          @for (img of v.photos; track img.id || img.url) {
            @if (img.url) {
              <img [src]="img.url" [alt]="''" (click)="lightboxSrc.set(img.url!)" class="aspect-video w-full cursor-zoom-in rounded-lg object-cover transition hover:opacity-90" />
            }
          }
        </div>
      </section>
    }

    <!-- Équipements (read-only — gérés par le owner) -->
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="mb-4 flex items-center justify-between gap-3">
        <h2 class="text-sm font-semibold text-gray-800 dark:text-white/90">Équipements</h2>
        <a routerLink="/options" class="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">
          Gérer le référentiel
        </a>
      </div>
      @if (optionsByCategory().length === 0) {
        <p class="text-sm text-gray-500 dark:text-gray-400">Aucun équipement rattaché à ce véhicule.</p>
      } @else {
        <div class="space-y-3">
          @for (group of optionsByCategory(); track group.category) {
            <div>
              <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {{ group.label }}
              </h3>
              <div class="flex flex-wrap gap-2">
                @for (opt of group.options; track opt.id) {
                  <a
                    [routerLink]="['/options', opt.id]"
                    class="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-gray-700 dark:bg-white/[0.03] dark:text-gray-200 dark:hover:bg-brand-500/10 dark:hover:text-brand-400"
                  >
                    {{ opt.nameFr }}
                  </a>
                }
              </div>
            </div>
          }
        </div>
      }
    </section>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <!-- Identité technique -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Identification</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Catégorie</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.category?.entityCode || v.category?.name || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Type</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.type?.entityCode || v.type?.name || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Marque</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.make?.entityCode || v.make?.name || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Modèle</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.model?.entityCode || v.model?.name || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Année</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.year }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Plaque</dt>
            <dd class="mt-1 font-mono text-sm text-gray-800 dark:text-white/90">{{ v.licencePlate || '—' }}</dd>
          </div>
        </dl>
      </section>

      <!-- Propriétaire -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Propriétaire</h2>
        @if (v.vehicleOwner; as o) {
          <app-user-cell [user]="o.user" [fallbackId]="o.userId" size="md" />
          <div class="mt-3 space-y-1">
            @if (o.businessName) {
              <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ o.businessName }}</p>
            }
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ o.ownerType === 'COMPANY' ? 'Société' : 'Particulier' }} · Propriétaire #{{ o.id }}
            </p>
          </div>
          <div class="mt-4 flex flex-wrap gap-2">
            <a [routerLink]="['/owners', o.id]" class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
              Fiche propriétaire
            </a>
            @if (o.userId) {
              <a [routerLink]="['/users', o.userId]" class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
                Compte utilisateur
              </a>
            }
          </div>
        } @else {
          <p class="text-xs text-gray-500 dark:text-gray-400">Aucun propriétaire rattaché.</p>
        }
      </section>

      <!-- Notes opérationnelles -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Notes véhicule (0–10)</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Sécurité</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.securityRate || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Confort</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.comfortRate || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Conso</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.fuelConsumptionRate || '—' }} L/100km</dd>
          </div>
        </dl>
      </section>

      <!-- Documents -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Documents</h2>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Carte grise</p>
            @if (v.registrationCard?.length) {
              <ul class="mt-2 space-y-1">
                @for (img of v.registrationCard; track img.id || img.url) {
                  @if (img.url) {
                    <li><a [href]="img.url" target="_blank" class="text-xs text-brand-600 hover:underline dark:text-brand-400">Voir le document</a></li>
                  }
                }
              </ul>
            } @else {
              <p class="mt-2 text-xs text-gray-400">—</p>
            }
          </div>
          <div>
            <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Assurance</p>
            @if (v.insuranceExpiryDate) {
              <p class="mt-0.5 text-xs font-medium"
                [class.text-error-600]="isExpired(v.insuranceExpiryDate)"
                [class.dark:text-error-400]="isExpired(v.insuranceExpiryDate)"
                [class.text-gray-600]="!isExpired(v.insuranceExpiryDate)"
                [class.dark:text-gray-400]="!isExpired(v.insuranceExpiryDate)">
                Expire le {{ v.insuranceExpiryDate | ldate:'dd MMM yyyy':'':'fr-SN' }}{{ isExpired(v.insuranceExpiryDate) ? ' — expirée' : '' }}
              </p>
            }
            @if (v.insurance?.length) {
              <ul class="mt-2 space-y-1">
                @for (img of v.insurance; track img.id || img.url) {
                  @if (img.url) {
                    <li><a [href]="img.url" target="_blank" class="text-xs text-brand-600 hover:underline dark:text-brand-400">Voir le document</a></li>
                  }
                }
              </ul>
            } @else {
              <p class="mt-2 text-xs text-gray-400">—</p>
            }
          </div>
          <div>
            <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Visite technique</p>
            @if (v.technicalInspectionExpiryDate) {
              <p class="mt-0.5 text-xs font-medium"
                [class.text-error-600]="isExpired(v.technicalInspectionExpiryDate)"
                [class.dark:text-error-400]="isExpired(v.technicalInspectionExpiryDate)"
                [class.text-gray-600]="!isExpired(v.technicalInspectionExpiryDate)"
                [class.dark:text-gray-400]="!isExpired(v.technicalInspectionExpiryDate)">
                Expire le {{ v.technicalInspectionExpiryDate | ldate:'dd MMM yyyy':'':'fr-SN' }}{{ isExpired(v.technicalInspectionExpiryDate) ? ' — expirée' : '' }}
              </p>
            }
            @if (v.technicalInspection?.length) {
              <ul class="mt-2 space-y-1">
                @for (img of v.technicalInspection; track img.id || img.url) {
                  @if (img.url) {
                    <li><a [href]="img.url" target="_blank" class="text-xs text-brand-600 hover:underline dark:text-brand-400">Voir le document</a></li>
                  }
                }
              </ul>
            } @else {
              <p class="mt-2 text-xs text-gray-400">—</p>
            }
          </div>
        </div>
        @if (v.provisionContract) {
          <a [href]="v.provisionContract" target="_blank" class="mt-4 inline-flex items-center gap-2 text-xs text-brand-600 hover:underline dark:text-brand-400">
            📄 Contrat de mise à disposition (PDF)
          </a>
        }
      </section>

      <!-- Workflow / métadonnées -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Workflow & métadonnées</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">ID</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">#{{ v.id }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Créé le</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.createdAt ? (v.createdAt | ldate:'dd MMM yyyy, HH:mm':'':'fr-SN') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Modifié le</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.updatedAt ? (v.updatedAt | ldate:'dd MMM yyyy, HH:mm':'':'fr-SN') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Mise à dispo (début)</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.provisionStartDate ? (v.provisionStartDate | ldate:'dd MMM yyyy':'':'fr-SN') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Mise à dispo (fin)</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.provisionEndDate ? (v.provisionEndDate | ldate:'dd MMM yyyy':'':'fr-SN') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Revue le</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.reviewedAt ? (v.reviewedAt | ldate:'dd MMM yyyy, HH:mm':'':'fr-SN') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">DocuSign</dt>
            <!-- Statut seul : le lien DocuSign est la session de signature DU
                 PROPRIÉTAIRE (#97). Le contrat signé (provisionContract) est
                 déjà exposé plus haut dans la fiche. -->
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.docusignSubmissionStatus || '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">QR code</dt>
            <dd class="mt-1 font-mono text-xs text-gray-500 break-all dark:text-gray-400">{{ v.qrcode || '—' }}</dd>
          </div>
        </dl>
      </section>
    </div>
  }
</div>

<!-- Approve modal -->
<app-confirm-modal
  [open]="approveOpen()"
  [busy]="approving()"
  [error]="approveError()"
  title="Approuver ce véhicule ?"
  confirmLabel="Approuver"
  busyLabel="Approbation…"
  (confirm)="confirmApprove()"
  (close)="closeApprove()"
>
  Le véhicule <span class="font-medium">{{ displayName() }}</span> deviendra disponible à la location une fois approuvé.
</app-confirm-modal>

<!-- Reject modal (custom — needs a reason field) -->
<app-modal [isOpen]="rejectOpen()" (close)="closeReject()" className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-dark">
  <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Rejeter ce véhicule</h3>
  <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
    Le propriétaire sera notifié du motif. Il pourra corriger et resoumettre.
  </p>
  <div class="mt-4">
    <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Motif <span class="text-error-500">*</span></label>
    <textarea
      [formControl]="rejectReasonCtrl"
      rows="3"
      placeholder="Ex. Carte grise illisible, plaque incohérente, etc."
      class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
    ></textarea>
    @if (rejectReasonCtrl.invalid && rejectReasonCtrl.touched) {
      <p class="mt-1.5 text-xs text-error-500">Motif requis (3 caractères min).</p>
    }
  </div>
  @if (rejectError(); as msg) {
    <div class="mt-3 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">{{ msg }}</div>
  }
  <div class="mt-6 flex justify-end gap-2">
    <button type="button" (click)="closeReject()" [disabled]="rejecting()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
    <button type="button" (click)="confirmReject()" [disabled]="rejecting() || rejectReasonCtrl.invalid" class="inline-flex items-center gap-2 rounded-lg bg-error-500 px-4 py-2 text-sm font-medium text-white hover:bg-error-600 disabled:opacity-60">
      @if (rejecting()) {
        <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
          <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
        </svg>
        Rejet…
      } @else { Confirmer le rejet }
    </button>
  </div>
</app-modal>

<!-- Delete modal -->
<app-confirm-modal
  [open]="deleteOpen()"
  [busy]="deleting()"
  [error]="deleteError()"
  title="Supprimer ce véhicule ?"
  confirmLabel="Supprimer définitivement"
  busyLabel="Suppression…"
  danger
  (confirm)="confirmDelete()"
  (close)="closeDelete()"
>
  Le véhicule <span class="font-medium">{{ displayName() }}</span> sera retiré de la flotte. Les locations passées restent en historique.
</app-confirm-modal>

<!-- Lightbox plein écran pour les photos -->
<app-image-lightbox [src]="lightboxSrc()" (close)="lightboxSrc.set(null)" />
